import { useState, useEffect } from 'react';
import { adfToHtml, isAdf } from '@/services/adf-to-html';
import * as api from '@/services/api';
import { getStatusColor } from '@/services/jira-status-config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faArrowUpRightFromSquare,
  faBolt,
  faBookmark,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCodeCommit,
  faCodePullRequest,
  faComment,
  faDownload,
  faFile,
  faFileLines,
  faFilm,
  faLayerGroup,
  faLink,
  faPaperclip,
  faSpinner,
  faUser,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

interface RelatedCommit {
  hash: string;
  message: string;
  author: string;
  date: string;
  branch: string;
  repoName: string;
  additions: number;
  deletions: number;
}

interface AvailableRepoRef {
  id: string;
  name: string;
  type: string;
  url?: string;
  path?: string;
}

// Fix relative Jira URLs (e.g. /rest/api/3/attachment/...) to absolute
function fixRelativeJiraUrls(html: string, jiraDomain: string): string {
  const baseUrl = `https://${jiraDomain}.atlassian.net`;
  return html
    .replace(/((?:src|href)=["'])(\/rest\/api\/[^"']+)/gi, `$1${baseUrl}$2`)
    .replace(/((?:src|href)=["'])(\/secure\/[^"']+)/gi, `$1${baseUrl}$2`);
}

interface JiraIssueDetailProps {
  issueKey: string;
  jiraDomain: string;
  jiraEmail: string;
  jiraApiToken: string;
  onClose: () => void;
  onNavigate?: (issueKey: string) => void;
  relatedCommits?: RelatedCommit[];
  availableRepos?: AvailableRepoRef[];
  storedUsername?: string | null;
  storedToken?: string | null;
}

// getStatusColor imported from @/services/jira-status-config

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateShort(d);
}

// Stable sub-components defined OUTSIDE the main component to prevent
// unmount/remount flashing on every parent re-render.

const PersonBadge = ({ name, avatarUrl, label }: { name?: string; avatarUrl?: string; label: string }) => {
  if (!name) return <span className="text-xs text-gray-500 dark:text-slate-500">Unassigned</span>;
  return (
    <div className="flex items-center gap-2">
      {avatarUrl ? <img src={avatarUrl} alt="" className="h-6 w-6 rounded-full" /> : <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-gray-400 dark:text-slate-500" />}
      <div>
        <div className="text-xs text-gray-900 dark:text-white font-medium">{name}</div>
        <div className="text-[10px] text-gray-500 dark:text-slate-500">{label}</div>
      </div>
    </div>
  );
};

const SectionHeader = ({ id, title, icon: Icon, count, expanded, onToggle }: {
  id: string; title: string; icon: any; count?: number; expanded: boolean; onToggle: (id: string) => void;
}) => (
  <button onClick={() => onToggle(id)}
    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-slate-700/20 transition-colors border-b border-gray-200 dark:border-slate-700/20">
    {expanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}
    <Icon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
    <span className="text-sm font-medium text-gray-900 dark:text-white">{title}</span>
    {count !== undefined && count > 0 && (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300">{count}</span>
    )}
  </button>
);

const IssueLinkBtn = ({ issueKey: key, summary, status, onClick }: {
  issueKey: string; summary?: string; status?: string; issuetype?: string; onClick: (key: string) => void;
}) => (
  <button onClick={() => onClick(key)}
    className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors text-left w-full group">
    <span className="text-xs font-mono text-blue-600 dark:text-blue-400 group-hover:text-blue-500 dark:group-hover:text-blue-300 flex-shrink-0">{key}</span>
    {status && <span className={`text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0 ${getStatusColor(status)}`}>{status}</span>}
    {summary && <span className="text-xs text-gray-600 dark:text-slate-300 truncate">{summary}</span>}
  </button>
);

export function JiraIssueDetail({ issueKey, jiraDomain, jiraEmail, jiraApiToken, onClose, onNavigate: _onNavigate, relatedCommits, availableRepos, storedUsername, storedToken }: JiraIssueDetailProps) {
  const [issue, setIssue] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['description', 'details', 'people', 'comments', 'subtasks', 'links', 'commits'])
  );
  const [expandedCommits, setExpandedCommits] = useState<Set<string>>(new Set());
  const [commitDiffs, setCommitDiffs] = useState<Record<string, string>>({});
  const [loadingDiffs, setLoadingDiffs] = useState<Set<string>>(new Set());
  const [commitVisibleLimit, setCommitVisibleLimit] = useState(10);
  const [processedDescription, setProcessedDescription] = useState<string | null>(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [processedComments, setProcessedComments] = useState<Record<string, string>>({});

  // Internal navigation: allow drilling into subtasks/linked issues
  const [currentKey, setCurrentKey] = useState(issueKey);
  const [navHistory, setNavHistory] = useState<string[]>([]);

  // Sync when parent issueKey changes
  useEffect(() => {
    setCurrentKey(issueKey);
    setNavHistory([]);
  }, [issueKey]);

  useEffect(() => {
    fetchIssue();
  }, [currentKey]);

  // Process description HTML to fix embedded media (videos, images) that use <embed>/<object>
  useEffect(() => {
    if (!issue?.description) { setProcessedDescription(null); return; }
    // Handle ADF JSON descriptions by converting to HTML first
    let html: string;
    if (isAdf(issue.description)) {
      html = adfToHtml(issue.description, issue.attachments);
    } else {
      html = issue.description as string;
    }
    // Fix relative Jira URLs before any processing
    html = fixRelativeJiraUrls(html, jiraDomain);
    let cancelled = false;

    const processMedia = async () => {
      // Find all <embed> tags and replace with proper elements
      const embedRegex = /<embed\s+[^>]*>/gi;
      const embeds = [...html.matchAll(embedRegex)];

      for (const match of embeds) {
        const tag = match[0];
        const srcMatch = tag.match(/src=["']([^"']+)["']/i);
        const typeMatch = tag.match(/type=["']([^"']+)["']/i);
        if (!srcMatch) continue;
        const src = srcMatch[1];
        const mimeType = typeMatch?.[1] || '';

        // Fetch the media through proxy and create a blob URL
        let blobUrl = src; // fallback to original
        try {
          const blob = await api.getJiraAttachment(jiraDomain, jiraEmail, jiraApiToken, src);
          blobUrl = URL.createObjectURL(blob);
        } catch { /* use original src as fallback */ }

        if (cancelled) return;

        if (mimeType.startsWith('video/')) {
          html = html.replace(tag, `<video controls class="max-w-full rounded-lg" style="max-height:400px"><source src="${blobUrl}" type="${mimeType === 'video/quicktime' ? 'video/mp4' : mimeType}">Your browser does not support this video.</video>`);
        } else if (mimeType.startsWith('image/')) {
          html = html.replace(tag, `<img src="${blobUrl}" class="max-w-full rounded-lg" />`);
        } else {
          html = html.replace(tag, `<a href="${blobUrl}" target="_blank" class="text-blue-400 hover:text-blue-300 text-xs">📎 Download attachment</a>`);
        }
      }

      // Fix <img> tags pointing to any Jira/Atlassian URLs that need auth
      // Matches /secure/attachment, /rest/api/3/attachment/content/, and any other atlassian.net image URLs
      const imgRegex = /<img\s+[^>]*src=["'](https?:\/\/[^"']*atlassian\.net[^"']+)["'][^>]*>/gi;
      const imgs = [...html.matchAll(imgRegex)];
      for (const match of imgs) {
        const src = match[1];
        try {
          const blob = await api.getJiraAttachment(jiraDomain, jiraEmail, jiraApiToken, src);
          const blobUrl = URL.createObjectURL(blob);
          html = html.replace(src, blobUrl);
        } catch { /* keep original */ }
        if (cancelled) return;
      }

      if (!cancelled) setProcessedDescription(html);
    };

    // Start with unprocessed HTML immediately, then swap in processed version
    setProcessedDescription(html);
    processMedia();

    return () => { cancelled = true; };
  }, [issue?.description, jiraDomain, jiraEmail, jiraApiToken]);

  // Process comment HTML — fix relative URLs and proxy images through auth
  useEffect(() => {
    if (!issue?.comments?.length) { setProcessedComments({}); return; }
    let cancelled = false;

    // Immediately set initial HTML with relative URLs fixed (images won't load yet without auth proxy)
    const initial: Record<string, string> = {};
    for (const c of issue.comments) {
      const body = c.body;
      let html = isAdf(body) ? adfToHtml(body, issue.attachments) : (typeof body === 'string' ? body : '');
      html = fixRelativeJiraUrls(html, jiraDomain);
      initial[c.id] = html;
    }
    setProcessedComments(initial);

    const processAllComments = async () => {
      const result: Record<string, string> = {};

      for (const c of issue.comments) {
        if (cancelled) return;
        const body = c.body;
        let html = isAdf(body) ? adfToHtml(body, issue.attachments) : (typeof body === 'string' ? body : '');
        if (!html) { result[c.id] = ''; continue; }
        html = fixRelativeJiraUrls(html, jiraDomain);

        // Process <embed> tags (videos, etc.)
        const embedRegex = /<embed\s+[^>]*>/gi;
        const embeds = [...html.matchAll(embedRegex)];
        for (const match of embeds) {
          const tag = match[0];
          const srcMatch = tag.match(/src=["']([^"']+)["']/i);
          const typeMatch = tag.match(/type=["']([^"']+)["']/i);
          if (!srcMatch) continue;
          const src = srcMatch[1];
          const mimeType = typeMatch?.[1] || '';
          let blobUrl = src;
          try {
            const blob = await api.getJiraAttachment(jiraDomain, jiraEmail, jiraApiToken, src);
            blobUrl = URL.createObjectURL(blob);
          } catch { /* fallback */ }
          if (cancelled) return;
          if (mimeType.startsWith('video/')) {
            html = html.replace(tag, `<video controls class="max-w-full rounded-lg" style="max-height:400px"><source src="${blobUrl}" type="${mimeType === 'video/quicktime' ? 'video/mp4' : mimeType}">Your browser does not support this video.</video>`);
          } else if (mimeType.startsWith('image/')) {
            html = html.replace(tag, `<img src="${blobUrl}" class="max-w-full rounded-lg" />`);
          } else {
            html = html.replace(tag, `<a href="${blobUrl}" target="_blank" class="text-blue-400 hover:text-blue-300 text-xs">\u{1F4CE} Download</a>`);
          }
        }

        // Proxy <img> tags pointing to atlassian.net through auth
        const imgRegex = /<img\s+[^>]*src=["'](https?:\/\/[^"']*atlassian\.net[^"']+)["'][^>]*>/gi;
        const imgs = [...html.matchAll(imgRegex)];
        for (const match of imgs) {
          const src = match[1];
          try {
            const blob = await api.getJiraAttachment(jiraDomain, jiraEmail, jiraApiToken, src);
            const blobUrl = URL.createObjectURL(blob);
            html = html.replace(src, blobUrl);
          } catch { /* keep original */ }
          if (cancelled) return;
        }

        result[c.id] = html;
      }

      if (!cancelled) setProcessedComments(result);
    };

    processAllComments();
    return () => { cancelled = true; };
  }, [issue?.comments, issue?.attachments, jiraDomain, jiraEmail, jiraApiToken]);

  const fetchIssue = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getJiraIssueDetail(jiraDomain, jiraEmail, jiraApiToken, currentKey);
      if ((data as any).error) {
        setError((data as any).error);
      } else {
        setIssue(data as any);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  };

  const handleIssueClick = (key: string) => {
    // Navigate in-place: push current key to history, load new key
    setNavHistory(prev => [...prev, currentKey]);
    setCurrentKey(key);
    setExpandedCommits(new Set());
    setCommitDiffs({});
    setCommitVisibleLimit(10);
    setDescriptionExpanded(false);
  };

  const handleBack = () => {
    const prev = [...navHistory];
    const backKey = prev.pop();
    if (backKey) {
      setNavHistory(prev);
      setCurrentKey(backKey);
      setExpandedCommits(new Set());
      setCommitDiffs({});
      setCommitVisibleLimit(10);
      setDescriptionExpanded(false);
    }
  };

  const toggleCommit = (commitId: string, repoName?: string) => {
    setExpandedCommits(prev => {
      const next = new Set(prev);
      if (next.has(commitId)) {
        next.delete(commitId);
      } else {
        next.add(commitId);
        if (!commitDiffs[commitId]) {
          fetchDiff(commitId, repoName);
        }
      }
      return next;
    });
  };

  const fetchDiff = async (commitHash: string, repoName?: string) => {
    setLoadingDiffs(prev => new Set(prev).add(commitHash));
    try {
      // Try to find matching repo config for POST endpoint (works with remote repos)
      let data: any = null;
      if (availableRepos && availableRepos.length > 0) {
        // Match by repo name if available, otherwise try all repos
        const candidates = repoName
          ? availableRepos.filter(r => r.name.toLowerCase() === repoName.toLowerCase())
          : availableRepos;
        const tryRepos = candidates.length > 0 ? candidates : availableRepos;
        for (const repo of tryRepos) {
          try {
            const config: any = { type: repo.type, url: repo.url, path: repo.path, name: repo.name, hash: commitHash };
            if (storedToken && (repo.type === 'bitbucket' || repo.type === 'github')) {
              config.username = storedUsername || '';
              config.appPassword = storedToken;
            }
            const result = await api.getCommitDiff(config, commitHash);
            if (result.diff && !result.diff.includes('Error loading diff')) {
              data = result;
              break;
            }
          } catch { /* try next repo */ }
        }
      }
      // Fallback: no diff available without repo context
      if (!data) {
        data = { diff: '// Could not load diff — no matching repository found' };
      }
      if (data?.diff) {
        setCommitDiffs(prev => ({ ...prev, [commitHash]: data.diff }));
      } else {
        setCommitDiffs(prev => ({ ...prev, [commitHash]: '// Could not load diff — commit may be in a remote repository' }));
      }
    } catch {
      setCommitDiffs(prev => ({ ...prev, [commitHash]: '// Could not load diff' }));
    } finally {
      setLoadingDiffs(prev => { const n = new Set(prev); n.delete(commitHash); return n; });
    }
  };

  // Merge Jira's dev commits with our locally-matched commits, deduped by hash
  const mergedCommits = (() => {
    const jiraCommits: any[] = issue?.devDetail?.commits || [];
    const seenHashes = new Set(jiraCommits.map((c: any) => (c.id || '').toLowerCase()));
    const localOnly = (relatedCommits || []).filter(c => !seenHashes.has(c.hash.toLowerCase()));
    // Convert local commits to the same shape as Jira commits
    const localAsJira = localOnly.map(c => ({
      id: c.hash,
      message: c.message,
      author: c.author,
      authorDate: c.date,
      repo: c.repoName,
      files: undefined,
      url: undefined,
      _isLocal: true,
      _additions: c.additions,
      _deletions: c.deletions,
      _branch: c.branch,
    }));
    return [...jiraCommits, ...localAsJira];
  })();

  return (
    <div className="bg-white/95 dark:bg-slate-800/60 border-t border-b border-blue-500/20 animate-in slide-in-from-top-1 duration-200">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800/80">
        <div className="flex items-center gap-2 min-w-0">
          {navHistory.length > 0 && (
            <button onClick={handleBack} className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors flex-shrink-0" title="Back">
              <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5 text-blue-400" />
            </button>
          )}
          {loading ? (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 text-blue-400 animate-spin" />
              <span className="text-xs text-gray-500 dark:text-slate-400">Loading {currentKey}...</span>
            </div>
          ) : issue ? (
            <>
              {issue.issuetype?.iconUrl && <img src={issue.issuetype.iconUrl} alt="" className="h-4 w-4 flex-shrink-0" />}
              <span className="text-xs font-mono text-blue-400 flex-shrink-0">{issue.key}</span>
              <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">{issue.summary}</span>
              {navHistory.length > 0 && (
                <span className="text-[10px] text-slate-600 flex-shrink-0">← {navHistory[navHistory.length - 1]}</span>
              )}
            </>
          ) : (
            <span className="text-xs text-red-400">{error || 'Failed to load'}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          {issue?.url && (
            <a href={issue.url} target="_blank" rel="noopener noreferrer"
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors text-gray-400 dark:text-slate-400 hover:text-blue-400"
              title="Open in Jira">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5" />
            </a>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors">
            <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" />
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 text-blue-400 animate-spin" />
        </div>
      )}

      {error && !loading && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-red-400" />
            <p className="text-xs text-red-300">{error}</p>
          </div>
        </div>
      )}

      {issue && !loading && (
        <div className="divide-y divide-gray-200 dark:divide-slate-700/15">

              {/* Status bar */}
              <div className="px-5 py-3 flex items-center gap-3 flex-wrap bg-gray-50 dark:bg-slate-800/30">
                {issue.status && (
                  <span className={`px-3 py-1 rounded-md text-xs font-bold border ${getStatusColor(issue.status.name)}`}>
                    {issue.status.name}
                  </span>
                )}
                {issue.issuetype && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                    {issue.issuetype.iconUrl && <img src={issue.issuetype.iconUrl} alt="" className="h-4 w-4" />}
                    {issue.issuetype.name}
                  </span>
                )}
                {issue.priority && (
                  <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-slate-400">
                    {issue.priority.iconUrl && <img src={issue.priority.iconUrl} alt="" className="h-4 w-4" />}
                    {issue.priority.name}
                  </span>
                )}
                {issue.resolution && (
                  <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                    <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> {issue.resolution.name}
                  </span>
                )}
                {issue.storyPoints && (
                  <span className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    <FontAwesomeIcon icon={faBolt} className="h-3 w-3" /> {issue.storyPoints} pts
                  </span>
                )}
                {issue.sprint && (
                  <span className="text-xs text-cyan-600 dark:text-cyan-400 flex items-center gap-1">
                    <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" /> {issue.sprint.name}
                  </span>
                )}
              </div>

              {/* People */}
              <div>
                <SectionHeader id="people" title="People" icon={mkIcon(faUsers)} expanded={expandedSections.has('people')} onToggle={toggleSection} />
                {expandedSections.has('people') && (
                  <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <PersonBadge name={issue.assignee?.displayName} avatarUrl={issue.assignee?.avatarUrl} label="Assignee" />
                    <PersonBadge name={issue.reporter?.displayName} avatarUrl={issue.reporter?.avatarUrl} label="Reporter" />
                    <PersonBadge name={issue.creator?.displayName} avatarUrl={issue.creator?.avatarUrl} label="Creator" />
                  </div>
                )}
              </div>

              {/* Details grid */}
              <div>
                <SectionHeader id="details" title="Details" icon={mkIcon(faFileLines)} expanded={expandedSections.has('details')} onToggle={toggleSection} />
                {expandedSections.has('details') && (
                  <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Created</div>
                      <div className="text-xs text-gray-700 dark:text-slate-300">{formatDate(issue.created)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Updated</div>
                      <div className="text-xs text-gray-700 dark:text-slate-300">{formatDate(issue.updated)}</div>
                    </div>
                    {issue.resolutiondate && (
                      <div>
                        <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Resolved</div>
                        <div className="text-xs text-gray-700 dark:text-slate-300">{formatDate(issue.resolutiondate)}</div>
                      </div>
                    )}
                    {issue.duedate && (
                      <div>
                        <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Due Date</div>
                        <div className="text-xs text-gray-700 dark:text-slate-300">{formatDateShort(issue.duedate)}</div>
                      </div>
                    )}
                    {(issue.timeTracking.originalEstimateLabel || issue.timeTracking.timeSpentLabel) && (
                      <>
                        <div>
                          <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Estimate</div>
                          <div className="text-xs text-gray-700 dark:text-slate-300">{issue.timeTracking.originalEstimateLabel || '—'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Time Spent</div>
                          <div className="text-xs text-gray-700 dark:text-slate-300">{issue.timeTracking.timeSpentLabel || '—'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Remaining</div>
                          <div className="text-xs text-gray-700 dark:text-slate-300">{issue.timeTracking.remainingEstimate || '—'}</div>
                        </div>
                      </>
                    )}
                    {issue.labels.length > 0 && (
                      <div className="col-span-2">
                        <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">Labels</div>
                        <div className="flex flex-wrap gap-1">
                          {issue.labels.map((l: string) => (
                            <span key={l} className="text-[10px] px-1.5 py-0.5 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border border-indigo-500/20 rounded">{l}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {issue.components.length > 0 && (
                      <div className="col-span-2">
                        <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">Components</div>
                        <div className="flex flex-wrap gap-1">
                          {issue.components.map((c: string) => (
                            <span key={c} className="text-[10px] px-1.5 py-0.5 bg-teal-500/10 text-teal-700 dark:text-teal-300 border border-teal-500/20 rounded">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {issue.fixVersions.length > 0 && (
                      <div className="col-span-2">
                        <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">Fix Versions</div>
                        <div className="flex flex-wrap gap-1">
                          {issue.fixVersions.map((v: any) => (
                            <span key={v.name} className="text-[10px] px-1.5 py-0.5 bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20 rounded">
                              {v.name} {v.released ? '✓' : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* Dev info — use mergedCommits count to include locally-matched commits */}
                    {(mergedCommits.length > 0 || issue.devInfo.pullRequests > 0) && (
                      <div className="col-span-2">
                        <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">Development</div>
                        <div className="flex items-center gap-3">
                          {mergedCommits.length > 0 && (
                            <span className="text-xs text-orange-400 flex items-center gap-1">
                              <FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5" /> {mergedCommits.length} commit{mergedCommits.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {issue.devInfo.pullRequests > 0 && (
                            <span className="text-xs text-blue-400 flex items-center gap-1">
                              <FontAwesomeIcon icon={faCodePullRequest} className="h-3.5 w-3.5" /> {issue.devInfo.pullRequests} PR{issue.devInfo.pullRequests !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <SectionHeader id="description" title="Description" icon={mkIcon(faFileLines)} expanded={expandedSections.has('description')} onToggle={toggleSection} />
                {expandedSections.has('description') && (
                  <div className="px-5 py-3">
                    {processedDescription ? (
                      <div className="relative">
                        <div
                          className={`prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-slate-300 text-sm leading-relaxed overflow-hidden transition-all
                            [&_h1]:text-base [&_h1]:text-gray-900 dark:[&_h1]:text-white [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2
                            [&_h2]:text-sm [&_h2]:text-gray-900 dark:[&_h2]:text-white [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5
                            [&_h3]:text-sm [&_h3]:text-gray-800 dark:[&_h3]:text-slate-200 [&_h3]:font-medium
                            [&_p]:mb-2 [&_ul]:mb-2 [&_ol]:mb-2
                            [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:hover:text-blue-500 dark:[&_a]:hover:text-blue-300
                            [&_code]:text-xs [&_code]:bg-gray-100 dark:[&_code]:bg-slate-800 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded
                            [&_pre]:bg-gray-100 dark:[&_pre]:bg-slate-800 [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto
                            [&_img]:max-w-full [&_img]:rounded-lg
                            [&_video]:max-w-full [&_video]:rounded-lg [&_video]:my-2
                            [&_table]:text-xs [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_th]:bg-gray-100 dark:[&_th]:bg-slate-800`}
                          style={!descriptionExpanded ? { maxHeight: '14em' } : undefined}
                          dangerouslySetInnerHTML={{ __html: processedDescription }} />
                        <div className="pt-2 text-center">
                          <button
                            onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                            className="text-[10px] font-medium px-4 py-1 rounded-md bg-gray-200/60 dark:bg-slate-700/60 text-gray-700 dark:text-slate-300 hover:bg-gray-300/70 dark:hover:bg-slate-600/70 transition-colors backdrop-blur-sm"
                          >
                            {descriptionExpanded ? 'Show less' : 'Show more'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 italic">No description</p>
                    )}
                  </div>
                )}
              </div>

              {/* Commits & Pull Requests */}
              {(mergedCommits.length > 0 || issue.devDetail?.pullRequests?.length > 0) && (
                <div>
                  <SectionHeader id="commits" title="Commits & Pull Requests" icon={mkIcon(faCodeCommit)}
                    count={mergedCommits.length + (issue.devDetail?.pullRequests?.length || 0)} expanded={expandedSections.has('commits')} onToggle={toggleSection} />
                  {expandedSections.has('commits') && (
                    <div className="px-4 py-2 space-y-1">
                      {/* PRs first */}
                      {issue.devDetail.pullRequests?.map((pr: any) => (
                        <div key={pr.id} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/20">
                          <FontAwesomeIcon icon={faCodePullRequest} className="h-4 w-4 text-blue-400 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {pr.url ? (
                                <a href={pr.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:text-blue-300 font-medium truncate">
                                  {pr.title || `PR #${pr.id}`}
                                </a>
                              ) : (
                                <span className="text-xs text-gray-900 dark:text-white font-medium truncate">{pr.title || `PR #${pr.id}`}</span>
                              )}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                pr.status === 'MERGED' ? 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/20' :
                                pr.status === 'OPEN' ? 'bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/20' :
                                'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/20'
                              }`}>{pr.status}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {pr.source && <span className="text-[10px] text-slate-500 font-mono">{pr.source}</span>}
                              {pr.source && pr.destination && <span className="text-[10px] text-slate-600">→</span>}
                              {pr.destination && <span className="text-[10px] text-slate-500 font-mono">{pr.destination}</span>}
                              {pr.repo && <span className="text-[10px] text-slate-600 ml-auto">{pr.repo}</span>}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Commits */}
                      {mergedCommits.slice(0, commitVisibleLimit).map((commit: any) => {
                        const shortHash = (commit.id || '').slice(0, 7);
                        const isExpanded = expandedCommits.has(commit.id);
                        const diff = commitDiffs[commit.id];
                        const isDiffLoading = loadingDiffs.has(commit.id);
                        return (
                          <div key={commit.id} className="rounded-lg bg-gray-50 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/20 overflow-hidden">
                            <button
                              onClick={() => toggleCommit(commit.id, commit.repo)}
                              className="w-full flex items-start gap-2 p-2 text-left hover:bg-gray-100 dark:hover:bg-slate-700/20 transition-colors">
                              <FontAwesomeIcon icon={faChevronRight} className={`h-3 w-3 text-slate-600 mt-0.5 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90 text-orange-400' : ''}`} />
                              <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4 text-orange-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-mono text-orange-400">{shortHash}</span>
                                  <span className="text-xs text-gray-700 dark:text-slate-300 truncate">{commit.message?.split('\n')[0]}</span>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[10px] text-slate-500">{commit.author}</span>
                                  {commit.authorDate && <span className="text-[10px] text-slate-600">{timeAgo(commit.authorDate)}</span>}
                                  {commit.repo && <span className="text-[10px] text-slate-600 ml-auto">{commit.repo}</span>}
                                  {commit.files && <span className="text-[10px] text-slate-600">{commit.files} file{commit.files !== 1 ? 's' : ''}</span>}
                                </div>
                              </div>
                              {commit.url && (
                                <a href={commit.url} target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 hover:bg-slate-700 rounded transition-colors text-slate-500 hover:text-blue-400 flex-shrink-0"
                                  title="Open in browser">
                                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3" />
                                </a>
                              )}
                            </button>
                            {isExpanded && (
                              <div className="border-t border-gray-200 dark:border-slate-700/20">
                                {isDiffLoading ? (
                                  <div className="flex items-center gap-2 px-4 py-3">
                                    <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 text-blue-400 animate-spin" />
                                    <span className="text-xs text-slate-500">Loading diff...</span>
                                  </div>
                                ) : diff ? (
                                  <pre className="text-[11px] leading-relaxed overflow-x-auto max-h-80 p-3 font-mono bg-gray-50 dark:bg-slate-900/50">
                                    {diff.split('\n').map((line: string, i: number) => {
                                      let cls = 'text-slate-400';
                                      if (line.startsWith('+') && !line.startsWith('+++')) cls = 'text-emerald-400 bg-emerald-500/5';
                                      else if (line.startsWith('-') && !line.startsWith('---')) cls = 'text-red-400 bg-red-500/5';
                                      else if (line.startsWith('@@')) cls = 'text-cyan-400';
                                      else if (line.startsWith('diff ') || line.startsWith('index ')) cls = 'text-slate-500 font-bold';
                                      return <div key={i} className={cls}>{line || ' '}</div>;
                                    })}
                                  </pre>
                                ) : (
                                  <div className="px-4 py-3 text-xs text-slate-500">No diff available</div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {mergedCommits.length > commitVisibleLimit && (
                        <div className="py-2 text-center">
                          <button
                            onClick={() => setCommitVisibleLimit(prev => prev + 10)}
                            className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Show {Math.min(mergedCommits.length - commitVisibleLimit, 10)} more commit{Math.min(mergedCommits.length - commitVisibleLimit, 10) !== 1 ? 's' : ''} ({mergedCommits.length - commitVisibleLimit} remaining)
                          </button>
                        </div>
                      )}
                      {commitVisibleLimit > 10 && mergedCommits.length > 10 && (
                        <div className="py-1.5 text-center">
                          <button
                            onClick={() => setCommitVisibleLimit(10)}
                            className="text-[10px] text-slate-500 hover:text-slate-400 transition-colors"
                          >
                            Show fewer
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Parent */}
              {issue.parent && (
                <div>
                  <SectionHeader id="parent" title="Parent" icon={mkIcon(faBookmark)} expanded={expandedSections.has('parent')} onToggle={toggleSection} />
                  {expandedSections.has('parent') && (
                    <div className="px-5 py-2">
                      <IssueLinkBtn issueKey={issue.parent.key} summary={issue.parent.summary} status={issue.parent.status} issuetype={issue.parent.issuetype} onClick={handleIssueClick} />
                    </div>
                  )}
                </div>
              )}

              {/* Subtasks */}
              {issue.subtasks.length > 0 && (
                <div>
                  <SectionHeader id="subtasks" title="Subtasks" icon={mkIcon(faLayerGroup)} count={issue.subtasks.length} expanded={expandedSections.has('subtasks')} onToggle={toggleSection} />
                  {expandedSections.has('subtasks') && (
                    <div className="px-3 py-1">
                      {issue.subtasks.map((s: any) => (
                        <div key={s.key} className="flex items-center gap-2 px-2 py-1.5">
                          <IssueLinkBtn issueKey={s.key} summary={s.summary} status={s.status} issuetype={s.issuetype} onClick={handleIssueClick} />
                          {s.assignee && <span className="text-[10px] text-slate-500 flex-shrink-0 ml-auto">{s.assignee}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Issue Links */}
              {issue.issueLinks.length > 0 && (
                <div>
                  <SectionHeader id="links" title="Linked Issues" icon={mkIcon(faLink)} count={issue.issueLinks.length} expanded={expandedSections.has('links')} onToggle={toggleSection} />
                  {expandedSections.has('links') && (
                    <div className="px-3 py-1">
                      {issue.issueLinks.map((link: any, idx: number) => (
                        <div key={idx} className="flex items-center gap-2 px-2 py-1.5">
                          <span className="text-[10px] text-slate-500 w-24 flex-shrink-0 text-right">{link.label}</span>
                          {link.issue && (
                            <IssueLinkBtn issueKey={link.issue.key} summary={link.issue.summary} status={link.issue.status} issuetype={link.issue.issuetype} onClick={handleIssueClick} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Attachments */}
              {issue.attachments?.length > 0 && (
                <div>
                  <SectionHeader id="attachments" title="Attachments" icon={mkIcon(faPaperclip)} count={issue.attachments.length} expanded={expandedSections.has('attachments')} onToggle={toggleSection} />
                  {expandedSections.has('attachments') && (
                    <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {issue.attachments.map((att: any) => {
                        const isImage = att.mimeType?.startsWith('image/');
                        const isVideo = att.mimeType?.startsWith('video/');
                        const sizeStr = att.size < 1024 ? `${att.size} B`
                          : att.size < 1048576 ? `${(att.size / 1024).toFixed(1)} KB`
                          : `${(att.size / 1048576).toFixed(1)} MB`;
                        return (
                          <div key={att.id} className="group relative bg-gray-50 dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700/30 rounded-lg overflow-hidden">
                            {/* Preview area */}
                            {isImage ? (
                              <a href={att.content} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] overflow-hidden bg-gray-100 dark:bg-slate-900/50">
                                <img src={att.thumbnail || att.content} alt={att.filename} className="w-full h-full object-cover hover:scale-105 transition-transform duration-200" />
                              </a>
                            ) : isVideo ? (
                              <a href={att.content} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center aspect-[4/3] bg-gray-100 dark:bg-slate-900/50 hover:bg-gray-200 dark:hover:bg-slate-800 transition-colors">
                                <FontAwesomeIcon icon={faFilm} className="h-8 w-8 text-gray-400 dark:text-slate-500" />
                              </a>
                            ) : (
                              <a href={att.content} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center aspect-[4/3] bg-gray-100 dark:bg-slate-900/50 hover:bg-gray-200 dark:hover:bg-slate-800 transition-colors">
                                <FontAwesomeIcon icon={faFile} className="h-8 w-8 text-gray-400 dark:text-slate-500" />
                              </a>
                            )}
                            {/* Info bar */}
                            <div className="px-2 py-1.5 flex items-center gap-1.5">
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] text-gray-700 dark:text-slate-300 font-medium truncate" title={att.filename}>{att.filename}</p>
                                <p className="text-[9px] text-gray-400 dark:text-slate-500">{sizeStr}{att.author ? ` · ${att.author}` : ''}</p>
                              </div>
                              <a href={att.content} download={att.filename} target="_blank" rel="noopener noreferrer"
                                className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors" title="Download">
                                <FontAwesomeIcon icon={faDownload} className="h-3 w-3 text-gray-500 dark:text-slate-400" />
                              </a>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Comments */}
              {issue.comments.length > 0 && (
                <div>
                  <SectionHeader id="comments" title="Comments" icon={mkIcon(faComment)} count={issue.comments.length} expanded={expandedSections.has('comments')} onToggle={toggleSection} />
                  {expandedSections.has('comments') && (
                    <div className="px-5 py-2 space-y-3">
                      {issue.comments.map((c: any) => (
                        <div key={c.id} className="bg-gray-50 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/20 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {c.author.avatarUrl ? (
                                <img src={c.author.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
                              ) : (
                                <FontAwesomeIcon icon={faUser} className="h-4 w-4 text-slate-500" />
                              )}
                              <span className="text-xs font-medium text-gray-900 dark:text-white">{c.author.displayName}</span>
                            </div>
                            <span className="text-[10px] text-slate-500" title={formatDate(c.created)}>{timeAgo(c.created)}</span>
                          </div>
                          {(() => {
                            const htmlContent = processedComments[c.id];
                            if (htmlContent) {
                              return (
                                <div className="prose prose-sm dark:prose-invert max-w-none text-xs text-gray-700 dark:text-slate-300
                                  [&_p]:mb-1.5 [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_code]:text-[10px] [&_code]:bg-gray-100 dark:[&_code]:bg-slate-700 [&_code]:px-1 [&_code]:rounded
                                  [&_pre]:bg-gray-100 dark:[&_pre]:bg-slate-700 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-[10px]
                                  [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2
                                  [&_li]:mb-0.5 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 dark:[&_blockquote]:border-slate-600 [&_blockquote]:pl-3 [&_blockquote]:italic
                                  [&_table]:text-[10px] [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_th]:bg-gray-100 dark:[&_th]:bg-slate-800
                                  [&_h1]:text-sm [&_h1]:font-semibold [&_h2]:text-xs [&_h2]:font-semibold [&_h3]:text-xs [&_h3]:font-medium
                                  [&_hr]:border-gray-200 dark:[&_hr]:border-slate-700 [&_hr]:my-2
                                  [&_img]:max-w-full [&_img]:rounded-lg"
                                  dangerouslySetInnerHTML={{ __html: htmlContent }} />
                              );
                            }
                            return <p className="text-xs text-slate-500 italic">Empty comment</p>;
                          })()}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Remote Links */}
              {issue.remoteLinks.length > 0 && (
                <div>
                  <SectionHeader id="remoteLinks" title="External Links" icon={mkIcon(faArrowUpRightFromSquare)} count={issue.remoteLinks.length} expanded={expandedSections.has('remoteLinks')} onToggle={toggleSection} />
                  {expandedSections.has('remoteLinks') && (
                    <div className="px-5 py-2 space-y-1">
                      {issue.remoteLinks.map((rl: any, idx: number) => (
                        <a key={idx} href={rl.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors">
                          {rl.icon && <img src={rl.icon} alt="" className="h-4 w-4" />}
                          <span className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300">{rl.title || rl.url}</span>
                          <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3 text-gray-400 dark:text-slate-500 ml-auto" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Changelog */}
              {issue.changelog.length > 0 && (
                <div>
                  <SectionHeader id="changelog" title="History" icon={mkIcon(faArrowRightArrowLeft)} count={issue.changelog.length} expanded={expandedSections.has('changelog')} onToggle={toggleSection} />
                  {expandedSections.has('changelog') && (
                    <div className="px-4 py-2 space-y-1.5">
                      {issue.changelog.map((entry: any) => {
                        const FIELD_LABELS: Record<string, string> = {
                          'status': 'Status', 'assignee': 'Assignee', 'reporter': 'Reporter',
                          'priority': 'Priority', 'summary': 'Summary', 'description': 'Description',
                          'labels': 'Labels', 'sprint': 'Sprint', 'resolution': 'Resolution',
                          'issuetype': 'Type', 'fixversions': 'Fix Version', 'Fix Version': 'Fix Version',
                          'components': 'Components', 'Component': 'Components',
                          'timeoriginalestimate': 'Time Estimate', 'timeestimate': 'Remaining Estimate',
                          'timespent': 'Time Spent', 'worklogid': 'Work Log',
                          'duedate': 'Due Date', 'Start date': 'Start Date', 'startdate': 'Start Date',
                          'resolutiondate': 'Resolved', 'created': 'Created',
                          'Rank': 'Rank', 'rank': 'Rank',
                          'Story Points': 'Story Points', 'story_points': 'Story Points',
                          'Epic Link': 'Epic', 'Epic Name': 'Epic Name',
                          'Parent': 'Parent', 'IssueParentAssociation': 'Parent',
                          'Link': 'Link', 'RemoteIssueLink': 'External Link',
                          'Flagged': 'Flagged', 'flagged': 'Flagged',
                          'attachment': 'Attachment', 'Attachment': 'Attachment',
                          'comment': 'Comment', 'WorklogId': 'Work Log',
                        };

                        const friendlyFieldName = (raw: string) => {
                          return FIELD_LABELS[raw] || FIELD_LABELS[raw.toLowerCase()] ||
                            raw.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).replace(/_/g, ' ').trim();
                        };

                        const isDateValue = (val: string) =>
                          /^\d{4}-\d{2}-\d{2}/.test(val) || /^\d{1,2}\/\w{3}\/\d{2,4}$/.test(val);

                        const formatLocalDate = (val: string) => {
                          try {
                            const cleaned = val.replace(/\s+00:00:00\.0$/, '');
                            const d = new Date(cleaned);
                            if (isNaN(d.getTime())) return null;
                            return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
                          } catch { return null; }
                        };

                        const isTimeField = (f: string) =>
                          ['timeoriginalestimate', 'timeestimate', 'timespent'].includes(f.toLowerCase());

                        const formatSeconds = (val: string) => {
                          const secs = parseInt(val, 10);
                          if (isNaN(secs) || secs === 0) return val;
                          const h = Math.floor(secs / 3600);
                          const m = Math.floor((secs % 3600) / 60);
                          if (h >= 40) {
                            const w = Math.floor(h / 40);
                            const remH = h % 40;
                            return remH > 0 ? `${w}w ${remH}h` : `${w}w`;
                          }
                          if (h >= 8) {
                            const d = Math.floor(h / 8);
                            const remH = h % 8;
                            return remH > 0 ? `${d}d ${remH}h` : `${d}d`;
                          }
                          return m > 0 ? `${h}h ${m}m` : `${h}h`;
                        };

                        const fieldBadgeColor = (field: string) => {
                          const f = field.toLowerCase();
                          if (f === 'status') return 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/20';
                          if (f === 'assignee' || f === 'reporter') return 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/20';
                          if (f === 'sprint') return 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border-cyan-500/20';
                          if (f === 'priority') return 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/20';
                          if (f === 'labels' || f === 'label') return 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/20';
                          if (f === 'description' || f === 'summary') return 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20';
                          if (f === 'resolution') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20';
                          if (f.includes('time') || f.includes('worklog')) return 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/20';
                          if (f.includes('date') || f === 'start date') return 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/20';
                          if (f.includes('parent') || f.includes('link') || f.includes('epic')) return 'bg-pink-500/15 text-pink-700 dark:text-pink-300 border-pink-500/20';
                          if (f === 'fix version' || f === 'fixversions' || f === 'component') return 'bg-teal-500/15 text-teal-700 dark:text-teal-300 border-teal-500/20';
                          return 'bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/20';
                        };

                        const renderValue = (val: string, field: string, type: 'from' | 'to') => {
                          if (!val) return null;
                          const f = field.toLowerCase();

                          // Time fields — convert seconds to human-readable
                          if (isTimeField(field)) {
                            const formatted = formatSeconds(val);
                            return (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 border border-sky-500/15 inline-flex items-center ${
                                type === 'from' ? 'line-through opacity-60' : 'text-sky-700 dark:text-sky-300'
                              }`} title={`${val} seconds`}>{formatted}</span>
                            );
                          }

                          // Date fields — format locally, show raw on hover
                          if (isDateValue(val)) {
                            const local = formatLocalDate(val);
                            if (local) {
                              return (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/15 inline-flex items-center ${
                                  type === 'from' ? 'line-through text-gray-400 dark:text-slate-400' : 'text-rose-700 dark:text-rose-300'
                                }`} title={val}>{local}</span>
                              );
                            }
                          }

                          // Status values get status color badges
                          if (f === 'status') {
                            return (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center ${
                                type === 'from' ? 'line-through opacity-60 ' : ''
                              }${getStatusColor(val)}`}>{val}</span>
                            );
                          }
                          // Assignee values
                          if (f === 'assignee' || f === 'reporter') {
                            return (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/15 inline-flex items-center gap-1 ${
                                type === 'from' ? 'line-through opacity-60' : ''
                              }`}>
                                <FontAwesomeIcon icon={faUser} className="h-2.5 w-2.5" />{val}
                              </span>
                            );
                          }
                          // Long text (description, etc.) - truncate
                          if (val.length > 80) {
                            return (
                              <span className={`text-[10px] ${type === 'from' ? 'text-red-400/60 line-through' : 'text-emerald-400/80'} truncate max-w-[300px] inline-block align-bottom`}
                                title={val}>
                                {val.slice(0, 80)}…
                              </span>
                            );
                          }
                          // Default
                          return (
                            <span className={`text-[10px] font-medium ${
                              type === 'from' ? 'text-red-400/60 line-through' : 'text-emerald-700 dark:text-emerald-400/80'
                            }`}>{val}</span>
                          );
                        };

                        return (
                          <div key={entry.id} className="bg-gray-50 dark:bg-slate-800/20 border border-gray-200 dark:border-slate-700/15 rounded-lg p-2.5 hover:bg-gray-100 dark:hover:bg-slate-800/30 transition-colors">
                            <div className="flex items-center gap-2 mb-1.5">
                              <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                                <FontAwesomeIcon icon={faUser} className="h-3 w-3 text-gray-500 dark:text-slate-400" />
                              </div>
                              <span className="text-[11px] font-medium text-gray-900 dark:text-white">{entry.author}</span>
                              <span className="text-[10px] text-slate-600 ml-auto" title={formatDate(entry.created)}>{timeAgo(entry.created)}</span>
                            </div>
                            <div className="ml-7 space-y-1">
                              {entry.items.map((item: any, i: number) => (
                                <div key={i} className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${fieldBadgeColor(item.field)}`}>
                                    {friendlyFieldName(item.field)}
                                  </span>
                                  {item.from && renderValue(item.from, item.field, 'from')}
                                  {(item.from || item.to) && <span className="text-[10px] text-slate-600">→</span>}
                                  {item.to && renderValue(item.to, item.field, 'to')}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
      )}
    </div>
  );
}

export default JiraIssueDetail;
